import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    company: '',
    employeeCount: '',
    primaryGoal: 'implement-iam',
    painPoint: '',
    preferredContact: 'email',
    email: '',
    phone: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch('https://idnty.one/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            company: '',
            employeeCount: '',
            primaryGoal: 'implement-iam',
            painPoint: '',
            preferredContact: 'email',
            email: '',
            phone: ''
          });
        }, 3000);
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Failed to submit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us - Schedule Your IAM Assessment"
        description="Ready to secure your identity infrastructure? Schedule a free assessment with IDNTY and get a fixed-price proposal for your IAM needs."
        keywords="contact IDNTY, IAM assessment, identity consultation, schedule demo, cybersecurity consultation"
        ogTitle="Contact IDNTY - Schedule Your IAM Assessment"
        ogDescription="Ready to secure your identity infrastructure? Schedule a free assessment with IDNTY and get a fixed-price proposal for your IAM needs."
        canonicalUrl="https://idnty.one/contact"
      />
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="hero-title" style={{ marginBottom: '2rem' }}>
            SCHEDULE ASSESSMENT
          </h1>
          <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
            Tell us your situation. We'll respond with a fixed-price proposal.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <div className="grid-2">
            {/* Contact Form */}
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <h2 className="section-title" style={{ fontSize: '2rem', marginBottom: '2rem' }}>
                REQUEST ASSESSMENT
              </h2>
              
              {submitted ? (
                <div style={{
                  background: 'var(--success)',
                  color: 'var(--white)',
                  padding: '2rem',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Request received. We'll review your situation (usually same day) and be in touch.
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Company name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Current employee count
                    </label>
                    <select
                      name="employeeCount"
                      value={formData.employeeCount}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Select</option>
                      <option value="15-50">15-50</option>
                      <option value="50-100">50-100</option>
                      <option value="100-200">100-200</option>
                      <option value="200+">200+</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Primary goal
                    </label>
                    <select
                      name="primaryGoal"
                      value={formData.primaryGoal}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="implement-iam">Implement IAM</option>
                      <option value="soc2">Pursue SOC 2</option>
                      <option value="both">Both</option>
                      <option value="monitoring">Just monitoring</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Biggest access control pain right now
                    </label>
                    <textarea
                      name="painPoint"
                      value={formData.painPoint}
                      onChange={handleChange}
                      rows="4"
                      placeholder="e.g. shared passwords, manual offboarding, security questionnaires blocking deals..."
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Preferred contact
                    </label>
                    <select
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '2rem' }}>
                    <label className="label" style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        border: '2px solid var(--gray)',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn" 
                    style={{ width: '100%', textAlign: 'center', opacity: isSubmitting ? 0.7 : 1 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Request Assessment'}
                  </button>
                  
                  {error && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#fee2e2',
                      color: '#991b1b',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      {error}
                    </div>
                  )}
                </form>
              )}
            </div>

            {/* What happens next */}
            <div className="card-heavy" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
              <h3 className="feature-heading" style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>
                WHAT HAPPENS NEXT
              </h3>
              <ol style={{ paddingLeft: '1.5rem', margin: 0, lineHeight: '2', fontSize: '1rem' }}>
                <li>We'll review your situation (usually same day)</li>
                <li>30-minute assessment call to understand your current state</li>
                <li>Fixed-price proposal within 24 hours</li>
                <li>Start implementation within 2 weeks</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
